import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BadgeIndianRupee,
  Box,
  Boxes,
  Building2,
  CreditCard,
  Edit3,
  Gift,
  ImagePlus,
  LayoutDashboard,
  MonitorSmartphone,
  PackageCheck,
  Plus,
  Printer,
  FileText,
  Trash2,
  Save,
  Search,
  Settings,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import './ControlCenterSubscriptions.css';
const currency = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN')}`;
const ENV_API_URL = String(process.env.REACT_APP_API_URL || '').trim();
const PAGE_ORIGIN =
  typeof window !== 'undefined' && window.location?.origin
    ? String(window.location.origin).replace(/\/+$/, '')
    : '';
const ENV_BASE_URL = ENV_API_URL.replace(/\/+$/, '').replace(/\/api$/i, '');
const BASE_URL = PAGE_ORIGIN || ENV_BASE_URL || 'https://vakra.co.in';
const SUBSCRIPTION_API = `${BASE_URL}/api/control-center/subscriptions`;
const ADMIN_TOKEN_KEY = 'vakra_control_admin_token';
const VAKRA_COMPANY_ADDRESS = '201, 2nd Floor, Pinnacle Business Park, Andheri East, Mumbai, Maharashtra 400093';
const VAKRA_COMPANY_EMAIL = 'support@vakra.co.in';
const getAdminToken = () => {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
  } catch {
    return '';
  }
};
const getAdminHeaders = () => ({
  Authorization: `Bearer ${getAdminToken()}`,
});
const apiJsonRequest = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAdminToken()}`,
      ...(options.headers || {}),
    },
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};
const resolveAssetUrl = (value) => {
  const image = String(value || '').trim();
  if (!image) return '';
  if (/^(data:|blob:|https?:\/\/)/i.test(image)) return image;
  if (image.startsWith('/')) return `${String(BASE_URL).replace(/\/+$/, '')}${image}`;
  return image;
};
const ControlCenterSubscriptions = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [search, setSearch] = useState('');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showHardwareModal, setShowHardwareModal] = useState(false);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [invoiceApproval, setInvoiceApproval] = useState(null);
  const [invoiceAddressChecked, setInvoiceAddressChecked] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editingHardwareId, setEditingHardwareId] = useState(null);
  const [editingExtraId, setEditingExtraId] = useState(null);
  const [editingBundleId, setEditingBundleId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [hardware, setHardware] = useState([]);
  const [extras, setExtras] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [vendorSubscriptions, setVendorSubscriptions] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [vendorStats, setVendorStats] = useState({ total: 0, pending: 0, active: 0, expiring: 0, expired: 0, cancelled: 0 });
  const [vendorLoading, setVendorLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [planForm, setPlanForm] = useState({
    name: '',
    durationDays: '',
    price: '',
    status: 'active',
  });
  const [hardwareForm, setHardwareForm] = useState({
    name: '',
    type: 'Printer',
    model: '',
    price: '',
    status: 'active',
    image: '',
    includedText: '',
    warrantyEnabled: false,
    warrantyMonths: '',
    warrantyType: 'Manufacturer Warranty',
    warrantyTerms: '',
  });
  const [extraForm, setExtraForm] = useState({
    name: '',
    type: 'Accessory',
    unit: 'pcs',
    price: '',
    status: 'active',
    image: '',
  });
  const [vendorForm, setVendorForm] = useState({
    vendorKey: '',
    purchaseType: 'plan',
    planId: '',
    bundleId: '',
    additionalHardware: [],
    additionalExtras: [],
    startDate: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [bundleForm, setBundleForm] = useState({
    name: '',
    planId: '',
    selectedHardware: [],
    selectedExtras: [],
    pricingMode: 'final',
    finalPrice: '',
    discountPercent: '',
    status: 'active',
  });
  const showMessage = useCallback((text, type = 'success') => {
    setMessage(String(text || ''));
    setMessageType(type);
  }, []);
  const loadCatalog = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    const url = `${SUBSCRIPTION_API}/catalog`;
    try {
      const data = await apiJsonRequest(url, { method: 'GET' });
      setPlans(Array.isArray(data.plans) ? data.plans : []);
      setHardware(Array.isArray(data.hardware) ? data.hardware : []);
      setExtras(Array.isArray(data.extras) ? data.extras : []);
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
      if (showLoader) setMessage('');
    } catch (error) {
      console.error('Subscription catalog load error:', {
        url,
        status: error?.status,
        message: error?.message,
        data: error?.data,
      });
      const statusText = error?.status ? ` (HTTP ${error.status})` : '';
      showMessage(
        `${error?.message || 'Unable to load the subscription catalog.'}${statusText}`,
        'error'
      );
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [showMessage]);
  const loadVendorManagement = useCallback(async (showLoader = true) => {
    if (showLoader) setVendorLoading(true);
    try {
      const [subscriptionData, vendorData] = await Promise.all([
        apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions`, { method: 'GET' }),
        apiJsonRequest(`${SUBSCRIPTION_API}/management/vendors`, { method: 'GET' }),
      ]);
      setVendorSubscriptions(Array.isArray(subscriptionData.subscriptions) ? subscriptionData.subscriptions : []);
      setVendorStats(subscriptionData.stats || { total: 0, pending: 0, active: 0, expiring: 0, expired: 0, cancelled: 0 });
      setAvailableVendors(Array.isArray(vendorData.vendors) ? vendorData.vendors : []);
    } catch (error) {
      console.error('Vendor subscription management load error:', error);
      showMessage(error?.message || 'Unable to load vendor subscriptions.', 'error');
    } finally {
      if (showLoader) setVendorLoading(false);
    }
  }, [showMessage]);
  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);
  useEffect(() => {
    if (activeTab === 'vendors') loadVendorManagement();
  }, [activeTab, loadVendorManagement]);
  const menu = [
    { label: 'Dashboard', icon: LayoutDashboard },
    { label: 'Vendors', icon: Building2 },
    { label: 'Subscriptions', icon: CreditCard, active: true },
    { label: 'Payments', icon: BadgeIndianRupee },
    { label: 'Settings', icon: Settings },
  ];
  const planMap = useMemo(
    () => new Map(plans.map((item) => [Number(item.id), item])),
    [plans]
  );
  const hardwareMap = useMemo(
    () => new Map(hardware.map((item) => [Number(item.id), item])),
    [hardware]
  );
  const extraMap = useMemo(
    () => new Map(extras.map((item) => [Number(item.id), item])),
    [extras]
  );
  const getBundleNormalTotal = (bundleLike) => {
    const plan = planMap.get(Number(bundleLike.planId));
    const planTotal = Number(plan?.price || 0);
    const hardwareTotal = (bundleLike.hardwareItems || bundleLike.selectedHardware || [])
      .reduce((sum, item) => {
        const product = hardwareMap.get(
          Number(item.hardwareId || item.id || item)
        );
        const qty = Number(item.quantity || 1);
        return sum + Number(product?.price || 0) * qty;
      }, 0);
    const extraTotal = (bundleLike.extraItems || bundleLike.selectedExtras || [])
      .reduce((sum, item) => {
        const extra = extraMap.get(
          Number(item.extraId || item.id || item)
        );
        const qty = Number(item.quantity || 1);
        return sum + Number(extra?.price || 0) * qty;
      }, 0);
    return planTotal + hardwareTotal + extraTotal;
  };
  const selectedBundleNormalTotal = useMemo(
    () => getBundleNormalTotal(bundleForm),
    [bundleForm, planMap, hardwareMap, extraMap]
  );
  const calculatedBundle = useMemo(() => {
    const normalTotal = selectedBundleNormalTotal;
    if (bundleForm.pricingMode === 'percent') {
      const percent = Math.max(
        0,
        Math.min(100, Number(bundleForm.discountPercent || 0))
      );
      const saving = normalTotal * (percent / 100);
      const finalPrice = Math.max(0, normalTotal - saving);
      return {
        normalTotal,
        finalPrice,
        saving,
        discountPercent: percent,
      };
    }
    const finalPrice = Math.max(
      0,
      Number(bundleForm.finalPrice || 0)
    );
    const saving = Math.max(0, normalTotal - finalPrice);
    const discountPercent =
      normalTotal > 0 ? (saving / normalTotal) * 100 : 0;
    return {
      normalTotal,
      finalPrice,
      saving,
      discountPercent,
    };
  }, [
    bundleForm.finalPrice,
    bundleForm.discountPercent,
    bundleForm.pricingMode,
    selectedBundleNormalTotal,
  ]);
  const readImageFile = (file, onReady) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onReady(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  };
  const resetPlanForm = () => {
    setEditingPlanId(null);
    setPlanForm({
      name: '',
      durationDays: '',
      price: '',
      status: 'active',
    });
  };
  const openAddPlan = () => {
    resetPlanForm();
    setShowPlanModal(true);
  };
  const openEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name || '',
      durationDays: String(plan.durationDays || ''),
      price: String(plan.price ?? ''),
      status: plan.status || 'active',
    });
    setShowPlanModal(true);
  };
  const savePlan = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(planForm.name || '').trim(),
      durationDays: Number(planForm.durationDays),
      price: Number(planForm.price),
      status: planForm.status,
    };
    if (!payload.name || !Number.isInteger(payload.durationDays) || payload.durationDays <= 0 || !Number.isFinite(payload.price) || payload.price < 0) {
      showMessage('Plan name, valid duration, and valid price are required.', 'error');
      return;
    }
    setSaving(true);
    const url = editingPlanId
      ? `${SUBSCRIPTION_API}/management/plans/${editingPlanId}`
      : `${SUBSCRIPTION_API}/management/plans`;
    try {
      await apiJsonRequest(url, {
        method: editingPlanId ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      await loadCatalog(false);
      showMessage(editingPlanId ? 'POS plan updated successfully.' : 'POS plan created successfully.');
      resetPlanForm();
      setShowPlanModal(false);
    } catch (error) {
      console.error('POS plan save error:', {
        url,
        status: error?.status,
        message: error?.message,
        data: error?.data,
      });
      const statusText = error?.status ? ` (HTTP ${error.status})` : '';
      showMessage(`${error?.message || 'Unable to save the POS plan.'}${statusText}`, 'error');
    } finally {
      setSaving(false);
    }
  };
  const resetHardwareForm = () => {
    setEditingHardwareId(null);
    setHardwareForm({
      name: '',
      type: 'Printer',
      model: '',
      price: '',
      status: 'active',
      image: '',
      includedText: '',
      warrantyEnabled: false,
      warrantyMonths: '',
      warrantyType: 'Manufacturer Warranty',
      warrantyTerms: '',
    });
  };
  const openAddHardware = () => {
    resetHardwareForm();
    setShowHardwareModal(true);
  };
  const openEditHardware = (item) => {
    setEditingHardwareId(item.id);
    setHardwareForm({
      name: item.name || '',
      type: item.type || 'Printer',
      model: item.model || '',
      price: String(item.price ?? ''),
      status: item.status || 'active',
      image: item.image || '',
      includedText: Array.isArray(item.includedItems)
        ? item.includedItems.join('\n')
        : '',
      warrantyEnabled: Boolean(item.warrantyEnabled),
      warrantyMonths: item.warrantyMonths ? String(item.warrantyMonths) : '',
      warrantyType: item.warrantyType || 'Manufacturer Warranty',
      warrantyTerms: item.warrantyTerms || '',
    });
    setShowHardwareModal(true);
  };
  const saveHardware = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(hardwareForm.name || '').trim(),
      type: hardwareForm.type,
      model: String(hardwareForm.model || '').trim(),
      price: Number(hardwareForm.price),
      status: hardwareForm.status,
      image: hardwareForm.image || '',
      includedItems: String(hardwareForm.includedText || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean),
      warrantyEnabled: Boolean(hardwareForm.warrantyEnabled),
      warrantyMonths: hardwareForm.warrantyEnabled
        ? Number(hardwareForm.warrantyMonths)
        : null,
      warrantyType: hardwareForm.warrantyEnabled
        ? String(hardwareForm.warrantyType || '').trim()
        : '',
      warrantyTerms: hardwareForm.warrantyEnabled
        ? String(hardwareForm.warrantyTerms || '').trim()
        : '',
    };
    if (!payload.name || !Number.isFinite(payload.price) || payload.price < 0) {
      showMessage('Hardware name and a valid price are required.', 'error');
      return;
    }
    if (
      payload.warrantyEnabled &&
      (!Number.isInteger(payload.warrantyMonths) || payload.warrantyMonths <= 0)
    ) {
      showMessage('Enter a valid warranty duration in months when warranty is enabled.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingHardwareId) {
        await axios.put(`${SUBSCRIPTION_API}/hardware/${editingHardwareId}`, payload, { headers: getAdminHeaders() });
      } else {
        await axios.post(`${SUBSCRIPTION_API}/hardware`, payload, { headers: getAdminHeaders() });
      }
      await loadCatalog(false);
      showMessage(editingHardwareId ? 'Hardware updated successfully.' : 'Hardware added successfully.');
      resetHardwareForm();
      setShowHardwareModal(false);
    } catch (error) {
      showMessage(error?.response?.data?.error || 'Unable to save hardware.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const resetExtraForm = () => {
    setEditingExtraId(null);
    setExtraForm({
      name: '',
      type: 'Accessory',
      unit: 'pcs',
      price: '',
      status: 'active',
      image: '',
    });
  };
  const openAddExtra = () => {
    resetExtraForm();
    setShowExtraModal(true);
  };
  const openEditExtra = (item) => {
    setEditingExtraId(item.id);
    setExtraForm({
      name: item.name || '',
      type: item.type || 'Accessory',
      unit: item.unit || 'pcs',
      price: String(item.price ?? ''),
      status: item.status || 'active',
      image: item.image || '',
    });
    setShowExtraModal(true);
  };
  const saveExtra = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(extraForm.name || '').trim(),
      type: extraForm.type,
      unit: String(extraForm.unit || '').trim() || 'pcs',
      price: Number(extraForm.price),
      status: extraForm.status,
      image: extraForm.image || '',
    };
    if (!payload.name || !Number.isFinite(payload.price) || payload.price < 0) {
      showMessage('Extra item name and a valid price are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingExtraId) {
        await axios.put(`${SUBSCRIPTION_API}/extras/${editingExtraId}`, payload, { headers: getAdminHeaders() });
      } else {
        await axios.post(`${SUBSCRIPTION_API}/extras`, payload, { headers: getAdminHeaders() });
      }
      await loadCatalog(false);
      showMessage(editingExtraId ? 'Extra item updated successfully.' : 'Extra item added successfully.');
      resetExtraForm();
      setShowExtraModal(false);
    } catch (error) {
      showMessage(error?.response?.data?.error || 'Unable to save the extra item.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const toggleBundleExtra = (extraId) => {
    setBundleForm((current) => {
      const exists = current.selectedExtras.some(
        (item) => Number(item.extraId) === Number(extraId)
      );
      return {
        ...current,
        selectedExtras: exists
          ? current.selectedExtras.filter(
              (item) =>
                Number(item.extraId) !== Number(extraId)
            )
          : [
              ...current.selectedExtras,
              { extraId: Number(extraId), quantity: 1 },
            ],
      };
    });
  };
  const updateBundleExtraQty = (extraId, quantity) => {
    setBundleForm((current) => ({
      ...current,
      selectedExtras: current.selectedExtras.map((item) =>
        Number(item.extraId) === Number(extraId)
          ? {
              ...item,
              quantity: Math.max(1, Number(quantity || 1)),
            }
          : item
      ),
    }));
  };
  const toggleBundleHardware = (hardwareId) => {
    setBundleForm((current) => {
      const exists = current.selectedHardware.some(
        (item) => Number(item.hardwareId) === Number(hardwareId)
      );
      return {
        ...current,
        selectedHardware: exists
          ? current.selectedHardware.filter(
              (item) =>
                Number(item.hardwareId) !== Number(hardwareId)
            )
          : [
              ...current.selectedHardware,
              { hardwareId: Number(hardwareId), quantity: 1 },
            ],
      };
    });
  };
  const updateBundleHardwareQty = (hardwareId, quantity) => {
    setBundleForm((current) => ({
      ...current,
      selectedHardware: current.selectedHardware.map((item) =>
        Number(item.hardwareId) === Number(hardwareId)
          ? {
              ...item,
              quantity: Math.max(1, Number(quantity || 1)),
            }
          : item
      ),
    }));
  };
  const resetBundleForm = () => {
    setEditingBundleId(null);
    setBundleForm({
      name: '',
      planId: '',
      selectedHardware: [],
      selectedExtras: [],
      pricingMode: 'final',
      finalPrice: '',
      discountPercent: '',
      status: 'active',
    });
  };
  const openAddBundle = () => {
    resetBundleForm();
    setShowBundleModal(true);
  };
  const openEditBundle = (bundle) => {
    const normalTotal = getBundleNormalTotal(bundle);
    const finalPrice = Number(bundle.bundlePrice || 0);
    const saving = Math.max(0, normalTotal - finalPrice);
    const discountPercent =
      normalTotal > 0
        ? (saving / normalTotal) * 100
        : 0;
    setEditingBundleId(bundle.id);
    setBundleForm({
      name: bundle.name || '',
      planId: String(bundle.planId || ''),
      selectedHardware: (bundle.hardwareItems || []).map((item) => ({
        hardwareId: Number(item.hardwareId),
        quantity: Math.max(1, Number(item.quantity || 1)),
      })),
      selectedExtras: (bundle.extraItems || []).map((item) => ({
        extraId: Number(item.extraId),
        quantity: Math.max(1, Number(item.quantity || 1)),
      })),
      pricingMode: 'final',
      finalPrice: String(finalPrice),
      discountPercent: String(discountPercent.toFixed(2)),
      status: bundle.status || 'active',
    });
    setShowBundleModal(true);
  };
  const saveBundle = async (event) => {
    event.preventDefault();
    const payload = {
      name: String(bundleForm.name || '').trim(),
      planId: Number(bundleForm.planId),
      hardwareItems: bundleForm.selectedHardware.map((item) => ({
        hardwareId: Number(item.hardwareId),
        quantity: Math.max(1, Number(item.quantity || 1)),
      })),
      extraItems: bundleForm.selectedExtras.map((item) => ({
        extraId: Number(item.extraId),
        quantity: Math.max(1, Number(item.quantity || 1)),
      })),
      pricingMode: bundleForm.pricingMode,
      finalPrice: bundleForm.pricingMode === 'final' ? Number(bundleForm.finalPrice || 0) : undefined,
      discountPercent: bundleForm.pricingMode === 'percent' ? Number(bundleForm.discountPercent || 0) : undefined,
      status: bundleForm.status,
    };
    if (!payload.name || !payload.planId) {
      showMessage('Bundle name and POS plan are required.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingBundleId) {
        await axios.put(`${SUBSCRIPTION_API}/bundles/${editingBundleId}`, payload, { headers: getAdminHeaders() });
      } else {
        await axios.post(`${SUBSCRIPTION_API}/bundles`, payload, { headers: getAdminHeaders() });
      }
      await loadCatalog(false);
      showMessage(editingBundleId ? 'Bundle updated successfully.' : 'Bundle created successfully.');
      resetBundleForm();
      setShowBundleModal(false);
    } catch (error) {
      showMessage(error?.response?.data?.error || 'Unable to save the bundle.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const resetVendorForm = () => {
    setVendorForm({
      vendorKey: '',
      purchaseType: 'plan',
      planId: '',
      bundleId: '',
      additionalHardware: [],
      additionalExtras: [],
      startDate: new Date().toISOString().slice(0, 10),
      notes: '',
    });
  };
  const openVendorAssignment = () => {
    resetVendorForm();
    setMessage('');
    setShowVendorModal(true);
  };
  const toggleVendorHardware = (hardwareId) => {
    setVendorForm((current) => {
      const exists = current.additionalHardware.some((item) => Number(item.hardwareId) === Number(hardwareId));
      return {
        ...current,
        additionalHardware: exists
          ? current.additionalHardware.filter((item) => Number(item.hardwareId) !== Number(hardwareId))
          : [...current.additionalHardware, { hardwareId: Number(hardwareId), quantity: 1 }],
      };
    });
  };
  const updateVendorHardwareQty = (hardwareId, quantity) => {
    setVendorForm((current) => ({
      ...current,
      additionalHardware: current.additionalHardware.map((item) =>
        Number(item.hardwareId) === Number(hardwareId)
          ? { ...item, quantity: Math.max(1, Number(quantity || 1)) }
          : item
      ),
    }));
  };
  const toggleVendorExtra = (extraId) => {
    setVendorForm((current) => {
      const exists = current.additionalExtras.some((item) => Number(item.extraId) === Number(extraId));
      return {
        ...current,
        additionalExtras: exists
          ? current.additionalExtras.filter((item) => Number(item.extraId) !== Number(extraId))
          : [...current.additionalExtras, { extraId: Number(extraId), quantity: 1 }],
      };
    });
  };
  const updateVendorExtraQty = (extraId, quantity) => {
    setVendorForm((current) => ({
      ...current,
      additionalExtras: current.additionalExtras.map((item) =>
        Number(item.extraId) === Number(extraId)
          ? { ...item, quantity: Math.max(1, Number(quantity || 1)) }
          : item
      ),
    }));
  };
  const escapeAgreementHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
  const printVendorAgreement = (agreement) => {
    if (!agreement) return;
    const snapshot = typeof agreement.agreementSnapshot === 'string'
      ? (() => { try { return JSON.parse(agreement.agreementSnapshot); } catch { return null; } })()
      : agreement.agreementSnapshot;
    const purchase = snapshot?.purchase || null;
    const vendor = snapshot?.vendor || {};
    const items = Array.isArray(purchase?.items) ? purchase.items : [];
    const rows = items.map((item) => {
      const warranty = item.warranty || {};
      const warrantyText = warranty.enabled ? `${warranty.months || ''} Months ${warranty.type || 'Warranty'}`.trim() : '—';
      return `<tr><td>${escapeAgreementHtml(item.itemName || item.name || 'Item')}</td><td>${escapeAgreementHtml(item.itemType || '')}</td><td>${escapeAgreementHtml(item.quantity || 1)}</td><td>₹${Number(item.unitPrice || 0).toLocaleString('en-IN')}</td><td>₹${Number(item.lineTotal || 0).toLocaleString('en-IN')}</td><td>${escapeAgreementHtml(warrantyText)}</td></tr>`;
    }).join('');
    const printWindow = window.open('', '_blank', 'width=980,height=820');
    if (!printWindow) return showMessage('The print window was blocked. Please allow pop-ups in your browser.', 'error');
    printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeAgreementHtml(agreement.agreementNo || 'VAKRA Agreement')}</title><style>body{font-family:Arial,sans-serif;color:#111827;margin:0;padding:28px}.page{max-width:900px;margin:auto}.head{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111827;padding-bottom:16px}.head h1{margin:0;font-size:28px}.head p{margin:4px 0;font-size:13px;color:#4b5563}.section{margin-top:20px}.section h3{font-size:15px;margin:0 0 10px;border-bottom:1px solid #d1d5db;padding-bottom:6px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px}.row{font-size:13px}.row b{display:block;margin-bottom:2px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #d1d5db;padding:7px;text-align:left}th{background:#f3f4f6}.terms{white-space:pre-wrap;font-size:12px;line-height:1.55}.signature{display:grid;grid-template-columns:1fr 1fr;gap:70px;margin-top:52px}.signline{border-top:1px solid #111;padding-top:6px;font-size:12px}@media print{body{padding:0}.page{max-width:none}}</style></head><body><div class="page"><div class="head"><div><h1>VAKRA</h1><p>Service & Subscription Agreement</p></div><div><p><b>Agreement:</b> ${escapeAgreementHtml(agreement.agreementNo || '—')}</p><p><b>Version:</b> ${escapeAgreementHtml(agreement.agreementVersion || '1.0')}</p><p><b>Date:</b> ${escapeAgreementHtml(agreement.agreementDate || '—')}</p></div></div><div class="section"><h3>Vendor Details</h3><div class="grid"><div class="row"><b>Business</b>${escapeAgreementHtml(agreement.businessName || vendor.businessName || '—')}</div><div class="row"><b>Owner</b>${escapeAgreementHtml(agreement.ownerName || vendor.ownerName || '—')}</div><div class="row"><b>Mobile</b>${escapeAgreementHtml(agreement.mobile || vendor.mobile || '—')}</div><div class="row"><b>Email</b>${escapeAgreementHtml(agreement.email || vendor.email || '—')}</div><div class="row" style="grid-column:1/-1"><b>Address</b>${escapeAgreementHtml(agreement.address || vendor.address || '—')}</div></div></div><div class="section"><h3>Subscription / Purchase</h3><div class="grid"><div class="row"><b>Plan</b>${escapeAgreementHtml(agreement.planName || snapshot?.subscription?.planName || '—')}</div><div class="row"><b>Order ID</b>${escapeAgreementHtml(agreement.orderId || purchase?.id || '—')}</div><div class="row"><b>Start</b>${escapeAgreementHtml(agreement.startDate || '—')}</div><div class="row"><b>Expiry</b>${escapeAgreementHtml(agreement.expiryDate || '—')}</div><div class="row"><b>Final Amount</b>₹${Number(purchase?.finalAmount ?? agreement.amount ?? 0).toLocaleString('en-IN')}</div><div class="row"><b>Payment</b>${escapeAgreementHtml(agreement.paymentStatus || 'pending')}</div></div></div>${rows ? `<div class="section"><h3>Purchased Items</h3><table><thead><tr><th>Item</th><th>Type</th><th>Qty</th><th>Unit</th><th>Total</th><th>Warranty</th></tr></thead><tbody>${rows}</tbody></table></div>` : ''}<div class="section"><h3>Terms & Conditions</h3><div class="terms">${escapeAgreementHtml(agreement.terms || '')}</div></div><div class="signature"><div class="signline">Vendor Signature / Name</div><div class="signline">For VAKRA</div></div></div><script>window.onload=()=>window.print();<\/script></body></html>`);
    printWindow.document.close();
  };
  const buildVendorInvoiceHtml = (invoice) => {
    if (!invoice) return '';
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const escapeHtml = escapeAgreementHtml;
    const itemRows = items.map((item) => {
      const warrantyText = item.warrantyEnabled
        ? `${item.warrantyMonths || ''} Months ${item.warrantyType || 'Warranty'}`.trim()
        : '—';
      return `<tr><td>${escapeHtml(item.itemName || 'Item')}</td><td>${escapeHtml(item.itemType || '')}</td><td>${escapeHtml(item.quantity || 1)}</td><td>₹${Number(item.unitPrice || 0).toLocaleString('en-IN')}</td><td>₹${Number(item.lineTotal || 0).toLocaleString('en-IN')}</td><td>${escapeHtml(warrantyText)}</td></tr>`;
    }).join('');
    const invoiceNo = invoice.invoiceNo || `VKR-INV-${new Date().getFullYear()}-${String(invoice.orderId || invoice.subscriptionId || '').padStart(6, '0')}`;
    return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(invoiceNo)}</title><style>body{font-family:Arial,sans-serif;color:#111827;margin:0;padding:28px}.page{max-width:900px;margin:auto}.head{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111827;padding-bottom:16px}.head h1{margin:0;font-size:28px}.head p{margin:4px 0;font-size:13px;color:#4b5563}.section{margin-top:20px}.section h3{font-size:15px;margin:0 0 10px;border-bottom:1px solid #d1d5db;padding-bottom:6px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px}.row{font-size:13px}.row b{display:block;margin-bottom:2px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #d1d5db;padding:7px;text-align:left}th{background:#f3f4f6}.totals{margin-left:auto;width:360px;margin-top:18px}.totals div{display:flex;justify-content:space-between;padding:6px 0;font-size:13px}.totals .grand{border-top:2px solid #111827;margin-top:5px;padding-top:10px;font-size:16px;font-weight:800}.note{font-size:11px;color:#6b7280;margin-top:22px}.signature{display:grid;grid-template-columns:1fr 1fr;gap:70px;margin-top:52px}.signline{border-top:1px solid #111;padding-top:6px;font-size:12px}@media print{body{padding:0}.page{max-width:none}}</style></head><body><div class="page"><div class="head"><div><h1>VAKRA</h1><p>Subscription / Hardware Invoice</p><p><b>Address:</b> ${escapeHtml(VAKRA_COMPANY_ADDRESS)}</p><p><b>Email:</b> ${escapeHtml(VAKRA_COMPANY_EMAIL)}</p></div><div><p><b>Invoice No:</b> ${escapeHtml(invoiceNo)}</p><p><b>Date:</b> ${escapeHtml(invoice.invoiceDate || new Date().toISOString().slice(0,10))}</p><p><b>Order ID:</b> ${escapeHtml(invoice.orderId || '—')}</p></div></div><div class="section"><h3>Vendor Details</h3><div class="grid"><div class="row"><b>Business</b>${escapeHtml(invoice.businessName || '—')}</div><div class="row"><b>Owner</b>${escapeHtml(invoice.ownerName || '—')}</div><div class="row"><b>Mobile</b>${escapeHtml(invoice.mobile || '—')}</div><div class="row"><b>Email</b>${escapeHtml(invoice.email || '—')}</div></div></div><div class="section"><h3>Purchase Details</h3><div class="grid"><div class="row"><b>Plan</b>${escapeHtml(invoice.planName || '—')}</div><div class="row"><b>Bundle</b>${escapeHtml(invoice.bundleName || 'Plan Only')}</div><div class="row"><b>Start Date</b>${escapeHtml(invoice.startDate || '—')}</div><div class="row"><b>Expiry Date</b>${escapeHtml(invoice.expiryDate || '—')}</div><div class="row"><b>Payment Status</b>${escapeHtml(invoice.paymentStatus || 'pending')}</div><div class="row"><b>Payment Mode</b>${escapeHtml(invoice.paymentMode || '—')}</div></div></div><div class="section"><h3>Items</h3><table><thead><tr><th>Item</th><th>Type</th><th>Qty</th><th>Unit Price</th><th>Total</th><th>Warranty</th></tr></thead><tbody>${itemRows || '<tr><td colspan="6">No item details available</td></tr>'}</tbody></table><div class="totals"><div><span>Normal Total</span><strong>₹${Number(invoice.normalTotal || 0).toLocaleString('en-IN')}</strong></div><div><span>Discount</span><strong>- ₹${Number(invoice.discountAmount || 0).toLocaleString('en-IN')}</strong></div><div class="grand"><span>Final Amount</span><span>₹${Number(invoice.finalAmount || 0).toLocaleString('en-IN')}</span></div></div></div></div></body></html>`;
  };
  const requestInvoiceApproval = (subscription, action) => {
    setInvoiceAddressChecked(false);
    setInvoiceApproval({ subscription, action });
  };
  const confirmInvoiceApproval = () => {
    if (!invoiceApproval || !invoiceAddressChecked) return;
    const { subscription, action } = invoiceApproval;
    setInvoiceApproval(null);
    setInvoiceAddressChecked(false);
    if (action === 'print') openVendorInvoice(subscription);
    if (action === 'download') downloadVendorInvoice(subscription);
  };
  const printVendorInvoice = (invoice) => {
    const html = buildVendorInvoiceHtml(invoice);
    if (!html) return;
    const printWindow = window.open('', '_blank', 'width=980,height=820');
    if (!printWindow) return showMessage('The print window was blocked. Please allow pop-ups in your browser.', 'error');
    printWindow.document.write(html.replace('</body>', '<script>window.onload=()=>window.print();<\/script></body>'));
    printWindow.document.close();
  };
  const downloadVendorInvoice = async (subscription) => {
    if (saving) return;
    setSaving(true);
    try {
      showMessage('');
      const data = await apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions/${subscription.subscriptionId}/invoice`, { method: 'GET' });
      const invoice = data.invoice;
      if (String(invoice?.paymentStatus || '').toLowerCase() !== 'paid') {
        throw new Error('Confirm payment before downloading a paid invoice.');
      }
      const html2pdfModule = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default || html2pdfModule;
      const doc = new DOMParser().parseFromString(buildVendorInvoiceHtml(invoice), 'text/html');
      const element = doc.querySelector('.page');
      if (!element) throw new Error('Invoice PDF layout could not be found.');
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;top:0;left:-10000px;width:794px;background:#fff;color:#111827;z-index:-1;';
      const style = document.createElement('style');
      style.textContent = ' .vakra-pdf-page{padding:25px;font-family:Arial,sans-serif} .vakra-pdf-page .head{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #111827;padding-bottom:16px}.vakra-pdf-page .head h1{margin:0;font-size:28px}.vakra-pdf-page .head p{margin:4px 0;font-size:13px;color:#4b5563}.vakra-pdf-page .section{margin-top:20px}.vakra-pdf-page .section h3{font-size:15px;margin:0 0 10px;border-bottom:1px solid #d1d5db;padding-bottom:6px}.vakra-pdf-page .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px}.vakra-pdf-page .row{font-size:13px}.vakra-pdf-page .row b{display:block;margin-bottom:2px}.vakra-pdf-page table{width:100%;border-collapse:collapse;font-size:12px}.vakra-pdf-page th,.vakra-pdf-page td{border:1px solid #d1d5db;padding:7px;text-align:left}.vakra-pdf-page th{background:#f3f4f6}.vakra-pdf-page .totals{margin-left:auto;width:360px;margin-top:18px}.vakra-pdf-page .totals div{display:flex;justify-content:space-between;padding:6px 0;font-size:13px}.vakra-pdf-page .totals .grand{border-top:2px solid #111827;margin-top:5px;padding-top:10px;font-size:16px;font-weight:800}.vakra-pdf-page .note{font-size:11px;color:#6b7280;margin-top:22px}.vakra-pdf-page .signature{display:grid;grid-template-columns:1fr 1fr;gap:70px;margin-top:52px}.vakra-pdf-page .signline{border-top:1px solid #111;padding-top:6px;font-size:12px}';
      element.classList.add('vakra-pdf-page');
      host.appendChild(style);
      host.appendChild(document.importNode(element, true));
      document.body.appendChild(host);
      try {
        const filename = `${String(invoice.invoiceNo || 'VAKRA-Invoice').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
        await html2pdf().set({ margin: 7, filename, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, scrollY: 0 }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, pagebreak: { mode: ['css', 'legacy'] } }).from(host).save();
      } finally {
        host.remove();
      }
      showMessage('Paid invoice PDF downloaded successfully. You can now share it manually via WhatsApp.');
    } catch (error) {
      showMessage(error?.message || 'Unable to download the invoice PDF.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const markVendorPaid = async (subscription) => {
    if (saving || !subscription?.subscriptionId) return;
    const confirmed = window.confirm(`Have you received the payment?\n\nVendor: ${subscription.businessName || 'Vendor'}\nAmount: ${currency(subscription.finalAmount ?? subscription.amount)}\n\nConfirming will mark this payment as PAID.`);
    if (!confirmed) return;
    setSaving(true);
    try {
      const data = await apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions/${subscription.subscriptionId}/mark-paid`, { method: 'POST', body: JSON.stringify({}) });
      showMessage(data.message || 'Payment marked as PAID successfully.');
      await loadVendorManagement(false);
    } catch (error) {
      showMessage(error?.message || 'Unable to update payment status.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const openVendorInvoice = async (subscription) => {
    try {
      showMessage('');
      const data = await apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions/${subscription.subscriptionId}/invoice`, { method: 'GET' });
      printVendorInvoice(data.invoice);
    } catch (error) {
      showMessage(error?.message || 'Unable to generate the invoice.', 'error');
    }
  };
  const openVendorAgreement = async (subscription) => {
    try {
      showMessage('');
      let agreement = null;
      try {
        const existing = await apiJsonRequest(`${BASE_URL}/api/control-center/agreements/subscription/${subscription.subscriptionId}`, { method: 'GET' });
        agreement = existing.agreement || null;
      } catch (lookupError) {
        if (Number(lookupError?.status) !== 404) throw lookupError;
      }
      if (!agreement) {
        const created = await apiJsonRequest(`${BASE_URL}/api/control-center/agreements`, {
          method: 'POST',
          body: JSON.stringify({
            subscriptionId: Number(subscription.subscriptionId),
            orderId: subscription.orderId ? Number(subscription.orderId) : null,
            agreementDate: new Date().toISOString().slice(0, 10),
            agreementVersion: '1.0',
          }),
        });
        const agreementId = Number(created?.agreement?.agreementId || 0);
        if (!agreementId) throw new Error('Agreement was created, but its ID was not returned.');
        const detail = await apiJsonRequest(`${BASE_URL}/api/control-center/agreements/${agreementId}`, { method: 'GET' });
        agreement = detail.agreement || created.agreement;
        showMessage(`Agreement ${agreement.agreementNo || ''} created successfully.`);
      }
      printVendorAgreement(agreement);
      await loadVendorManagement(false);
    } catch (error) {
      showMessage(error?.message || 'Unable to generate or open the agreement.', 'error');
    }
  };
  const deletePendingSubscription = async (subscription) => {
    if (!subscription?.subscriptionId) return;
    if (String(subscription.status || '').toLowerCase() !== 'pending' || String(subscription.paymentStatus || 'pending').toLowerCase() === 'paid') {
      return showMessage('Only unpaid pending subscriptions can be deleted.', 'error');
    }
    const confirmed = window.confirm(`Delete this pending subscription?\n\nVendor: ${subscription.businessName || 'Vendor'}\nPlan: ${subscription.planName || '—'}\nAmount: ${currency(subscription.finalAmount ?? subscription.amount)}\n\nIts pending order, order items, and pending agreement will also be deleted.`);
    if (!confirmed) return;
    setSaving(true);
    try {
      const data = await apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions/${subscription.subscriptionId}`, { method: 'DELETE' });
      showMessage(data?.message || 'Pending subscription deleted successfully.');
      await loadVendorManagement(false);
    } catch (error) {
      showMessage(error?.message || 'Unable to delete the pending subscription.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const saveVendorAssignment = async (event) => {
    event.preventDefault();
    const vendor = availableVendors.find((item) => item.vendorKey === vendorForm.vendorKey);
    if (!vendor) return showMessage('Please select a vendor.', 'error');
    const isBundle = vendorForm.purchaseType === 'bundle';
    if (isBundle && !vendorForm.bundleId) return showMessage('Please select a bundle.', 'error');
    if (!isBundle && !vendorForm.planId) return showMessage('Please select a POS plan.', 'error');
    if (!vendorForm.startDate) return showMessage('Please select a start date.', 'error');
    setSaving(true);
    try {
      const data = await apiJsonRequest(`${SUBSCRIPTION_API}/management/vendor-subscriptions`, {
        method: 'POST',
        body: JSON.stringify({
          sourceDatabase: vendor.sourceDatabase,
          vendorId: Number(vendor.originalVendorId),
          purchaseType: vendorForm.purchaseType,
          planId: isBundle ? null : Number(vendorForm.planId),
          bundleId: isBundle ? Number(vendorForm.bundleId) : null,
          additionalHardware: vendorForm.additionalHardware.map((item) => ({
            hardwareId: Number(item.hardwareId),
            quantity: Math.max(1, Number(item.quantity || 1)),
          })),
          additionalExtras: vendorForm.additionalExtras.map((item) => ({
            extraId: Number(item.extraId),
            quantity: Math.max(1, Number(item.quantity || 1)),
          })),
          startDate: vendorForm.startDate,
          notes: String(vendorForm.notes || '').trim(),
        }),
      });
      setShowVendorModal(false);
      resetVendorForm();
      showMessage(data?.message || 'Vendor subscription assigned successfully.');
      await loadVendorManagement(false);
    } catch (error) {
      showMessage(error?.message || 'Unable to assign the vendor subscription.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const selectedVendorBundle = bundles.find((item) => Number(item.id) === Number(vendorForm.bundleId));
  const selectedVendorPlan = plans.find((item) => Number(item.id) === Number(vendorForm.purchaseType === 'bundle' ? selectedVendorBundle?.planId : vendorForm.planId));
  const selectedVendorBaseAmount = vendorForm.purchaseType === 'bundle' ? Number(selectedVendorBundle?.bundlePrice || 0) : Number(selectedVendorPlan?.price || 0);
  const selectedVendorAdditionalHardwareTotal = vendorForm.additionalHardware.reduce((sum, item) => {
    const product = hardwareMap.get(Number(item.hardwareId));
    return sum + Number(product?.price || 0) * Math.max(1, Number(item.quantity || 1));
  }, 0);
  const selectedVendorAdditionalExtraTotal = vendorForm.additionalExtras.reduce((sum, item) => {
    const extra = extraMap.get(Number(item.extraId));
    return sum + Number(extra?.price || 0) * Math.max(1, Number(item.quantity || 1));
  }, 0);
  const selectedVendorAmount = selectedVendorBaseAmount + selectedVendorAdditionalHardwareTotal + selectedVendorAdditionalExtraTotal;
  const filteredVendorSubscriptions = vendorSubscriptions.filter((item) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [item.businessName, item.ownerName, item.mobile, item.planName, item.bundleName, item.paymentStatus, item.status, item.subscriptionId, item.orderId].join(' ').toLowerCase().includes(query);
  });
  const filteredPlans = plans.filter((item) =>
    item.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const filteredHardware = hardware.filter((item) =>
    [item.name, item.type, item.model]
      .join(' ')
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  return (
    <div className="vsub-app">
      <aside className="vsub-sidebar">
        <div className="vsub-brand">
          <div className="vsub-logo">V</div>
          <div>
            <strong>VAKRA</strong>
            <span>CONTROL CENTER</span>
          </div>
        </div>
        <nav>
          {menu.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                className={item.active ? 'active' : ''}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>
      <main className="vsub-main">
        <header className="vsub-topbar">
          <div>
            <span>VAKRA CONTROL CENTER</span>
            <h1>Subscription & Sales Setup</h1>
          </div>
          <div className="vsub-admin-chip">
            <div>A</div>
            <span>Administrator</span>
          </div>
        </header>
        <section className="vsub-content">
          {message ? (
            <div className={`vsub-feedback ${messageType === 'error' ? 'error' : 'success'}`}>
              <span>{message}</span>
              <button type="button" onClick={() => setMessage('')}>×</button>
            </div>
          ) : null}
          {loading ? (
            <div className="vsub-loading">Loading subscription data...</div>
          ) : null}
          <div className="vsub-hero">
            <div>
              <span>PRICING MANAGEMENT</span>
              <h2>POS, Hardware & Bundle Pricing</h2>
              <p>
                Manage POS and hardware pricing separately, then combine them into discounted bundles.
              </p>
            </div>
          </div>
          <div className="vsub-summary-grid">
            <article>
              <div className="blue">
                <MonitorSmartphone size={20} />
              </div>
              <span>POS Plans</span>
              <strong>{plans.length}</strong>
            </article>
            <article>
              <div className="orange">
                <Printer size={20} />
              </div>
              <span>Hardware Products</span>
              <strong>{hardware.length}</strong>
            </article>
            <article>
              <div className="purple">
                <Gift size={20} />
              </div>
              <span>Extra Items</span>
              <strong>{extras.length}</strong>
            </article>
            <article>
              <div className="green">
                <PackageCheck size={20} />
              </div>
              <span>Active Bundles</span>
              <strong>
                {bundles.filter((item) => item.status === 'active').length}
              </strong>
            </article>
          </div>
          <div className="vsub-tabs">
            <button
              type="button"
              className={activeTab === 'plans' ? 'active' : ''}
              onClick={() => setActiveTab('plans')}
            >
              <MonitorSmartphone size={17} />
              POS Plans
            </button>
            <button
              type="button"
              className={activeTab === 'hardware' ? 'active' : ''}
              onClick={() => setActiveTab('hardware')}
            >
              <Printer size={17} />
              Hardware
            </button>
            <button
              type="button"
              className={activeTab === 'extras' ? 'active' : ''}
              onClick={() => setActiveTab('extras')}
            >
              <Gift size={17} />
              Extra Items
            </button>
            <button
              type="button"
              className={activeTab === 'bundles' ? 'active' : ''}
              onClick={() => setActiveTab('bundles')}
            >
              <Boxes size={17} />
              Bundles
            </button>
            <button
              type="button"
              className={activeTab === 'vendors' ? 'active' : ''}
              onClick={() => setActiveTab('vendors')}
            >
              <Users size={17} />
              Vendor Subscriptions
            </button>
          </div>
          <div className="vsub-toolbar">
            <div className="vsub-search">
              <Search size={18} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search..."
              />
            </div>
            {activeTab === 'plans' ? (
              <button
                type="button"
                className="vsub-primary"
                onClick={openAddPlan}
              >
                <Plus size={18} />
                Add POS Plan
              </button>
            ) : null}
            {activeTab === 'hardware' ? (
              <button
                type="button"
                className="vsub-primary"
                onClick={openAddHardware}
              >
                <Plus size={18} />
                Add Hardware
              </button>
            ) : null}
            {activeTab === 'extras' ? (
              <button
                type="button"
                className="vsub-primary"
                onClick={openAddExtra}
              >
                <Plus size={18} />
                Add Extra Item
              </button>
            ) : null}
          {activeTab === 'bundles' ? (
              <button
                type="button"
                className="vsub-primary"
                onClick={openAddBundle}
              >
                <Plus size={18} />
                Create Bundle
              </button>
            ) : null}
            {activeTab === 'vendors' ? (
              <button type="button" className="vsub-primary" onClick={openVendorAssignment}>
                <Plus size={18} />
                Assign Subscription
              </button>
            ) : null}
          </div>
          {activeTab === 'extras' ? (
            <section className="vsub-card-grid">
              {extras.map((item) => (
                <article className="vsub-card" key={item.id}>
                  {item.image ? (
                    <div className="vsub-product-image">
                      <img src={resolveAssetUrl(item.image)} alt={item.name} />
                    </div>
                  ) : (
                    <div className="vsub-card-icon extra">
                      <Gift size={22} />
                    </div>
                  )}
                  <div className="vsub-card-head">
                    <div>
                      <strong>{item.name}</strong>
                      <span>
                        {item.type} • per {item.unit}
                      </span>
                    </div>
                    <span className={`vsub-status ${item.status}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="vsub-price">
                    {currency(item.price)}
                    <small>additional item price</small>
                  </div>
                  <button
                    type="button"
                    className="vsub-card-action"
                    onClick={() => openEditExtra(item)}
                  >
                    <Edit3 size={15} />
                    Edit Extra
                  </button>
                </article>
              ))}
            </section>
          ) : null}
          {activeTab === 'plans' ? (
            <section className="vsub-card-grid">
              {filteredPlans.map((plan) => (
                <article className="vsub-card" key={plan.id}>
                  <div className="vsub-card-icon plan">
                    <MonitorSmartphone size={22} />
                  </div>
                  <div className="vsub-card-head">
                    <div>
                      <strong>{plan.name}</strong>
                      <span>{plan.durationDays} days</span>
                    </div>
                    <span className={`vsub-status ${plan.status}`}>
                      {plan.status}
                    </span>
                  </div>
                  <div className="vsub-price">
                    {currency(plan.price)}
                    <small>POS charge</small>
                  </div>
                  <button
                    type="button"
                    className="vsub-card-action"
                    onClick={() => openEditPlan(plan)}
                  >
                    <Edit3 size={15} />
                    Edit Plan
                  </button>
                </article>
              ))}
            </section>
          ) : null}
          {activeTab === 'hardware' ? (
            <section className="vsub-card-grid">
              {filteredHardware.map((item) => (
                <article className="vsub-card" key={item.id}>
                  {item.image ? (
                    <div className="vsub-product-image">
                      <img src={resolveAssetUrl(item.image)} alt={item.name} />
                    </div>
                  ) : (
                    <div className="vsub-card-icon hardware">
                    {item.type === 'Printer' ? (
                      <Printer size={22} />
                    ) : (
                      <Box size={22} />
                    )}
                    </div>
                  )}
                  <div className="vsub-card-head">
                    <div>
                      <strong>{item.name}</strong>
                      <span>
                        {item.type}
                        {item.model ? ` • ${item.model}` : ''}
                      </span>
                    </div>
                    <span className={`vsub-status ${item.status}`}>
                      {item.status}
                    </span>
                  </div>
                  <div className="vsub-price">
                    {currency(item.price)}
                    <small>individual hardware price</small>
                  </div>
                  {Array.isArray(item.includedItems) &&
                  item.includedItems.length ? (
                    <div className="vsub-included-box">
                      <strong>What’s Included in the Box</strong>
                      {item.includedItems.map((includedItem) => (
                        <span key={includedItem}>
                          ✓ {includedItem}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {item.warrantyEnabled ? (
                    <div className="vsub-included-box">
                      <strong>Warranty</strong>
                      <span>
                        ✓ {Number(item.warrantyMonths || 0)} Months
                        {item.warrantyType ? ` • ${item.warrantyType}` : ''}
                      </span>
                      {item.warrantyTerms ? <span>{item.warrantyTerms}</span> : null}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="vsub-card-action"
                    onClick={() => openEditHardware(item)}
                  >
                    <Edit3 size={15} />
                    Edit Hardware
                  </button>
                </article>
              ))}
            </section>
          ) : null}
          {activeTab === 'bundles' ? (
            <section className="vsub-bundle-list">
              {bundles.map((bundle) => {
                const plan = planMap.get(Number(bundle.planId));
                const normalTotal = getBundleNormalTotal(bundle);
                const saving = Math.max(
                  0,
                  normalTotal - Number(bundle.bundlePrice || 0)
                );
                const percent =
                  normalTotal > 0 ? (saving / normalTotal) * 100 : 0;
                return (
                  <article className="vsub-bundle-card" key={bundle.id}>
                    <div className="vsub-bundle-top">
                      <div>
                        <span className="vsub-bundle-badge">
                          <Sparkles size={14} />
                          BUNDLE
                        </span>
                        <h3>{bundle.name}</h3>
                        <p>{plan?.name}</p>
                      </div>
                      <span className={`vsub-status ${bundle.status}`}>
                        {bundle.status}
                      </span>
                    </div>
                    <div className="vsub-bundle-items">
                      <div>
                        <span>POS</span>
                        <strong>{currency(plan?.price)}</strong>
                      </div>
                      {bundle.hardwareItems.map((bundleItem) => {
                        const product = hardwareMap.get(
                          Number(bundleItem.hardwareId)
                        );
                        return (
                          <div key={bundleItem.hardwareId}>
                            <span>
                              {product?.name}
                              {Number(bundleItem.quantity || 1) > 1
                                ? ` × ${bundleItem.quantity}`
                                : ''}
                            </span>
                            <strong>
                              {currency(
                                Number(product?.price || 0) *
                                  Number(bundleItem.quantity || 1)
                              )}
                            </strong>
                          </div>
                        );
                      })}
                      {(bundle.extraItems || []).map((bundleItem) => {
                        const extra = extraMap.get(
                          Number(bundleItem.extraId)
                        );
                        return (
                          <div key={`extra-${bundleItem.extraId}`}>
                            <span>
                              {extra?.name}
                              {Number(bundleItem.quantity || 1) > 1
                                ? ` × ${bundleItem.quantity}`
                                : ''}
                            </span>
                            <strong>
                              {currency(
                                Number(extra?.price || 0) *
                                  Number(bundleItem.quantity || 1)
                              )}
                            </strong>
                          </div>
                        );
                      })}
                    </div>
                    <div className="vsub-bundle-price-box">
                      <div>
                        <span>Normal Total</span>
                        <del>{currency(normalTotal)}</del>
                      </div>
                      <div>
                        <span>Bundle Price</span>
                        <strong>{currency(bundle.bundlePrice)}</strong>
                      </div>
                      <div className="saving">
                        <span>You Save</span>
                        <strong>
                          {currency(saving)} ({percent.toFixed(2)}% OFF)
                        </strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="vsub-card-action"
                      onClick={() => openEditBundle(bundle)}
                    >
                      <Edit3 size={15} />
                      Edit Bundle
                    </button>
                  </article>
                );
              })}
            </section>
          ) : null}
          {activeTab === 'vendors' ? (
            <section className="vsub-vendor-management">
              <div className="vsub-vendor-summary">
                <article><span>Total</span><strong>{vendorStats.total || 0}</strong></article>
                <article><span>Pending</span><strong>{vendorStats.pending || 0}</strong></article>
                <article><span>Active</span><strong>{vendorStats.active || 0}</strong></article>
                <article><span>Expiring</span><strong>{vendorStats.expiring || 0}</strong></article>
                <article><span>Expired</span><strong>{vendorStats.expired || 0}</strong></article>
              </div>
              {vendorLoading ? (
                <div className="vsub-loading">Loading vendor subscriptions...</div>
              ) : filteredVendorSubscriptions.length === 0 ? (
                <div className="vsub-vendor-placeholder">
                  <CreditCard size={42} />
                  <h3>No Vendor Subscriptions</h3>
                  <p>Click Assign Subscription to assign a POS plan or bundle to a vendor.</p>
                </div>
              ) : (
                <div className="vsub-vendor-table-wrap">
                  <table className="vsub-vendor-table">
                    <thead>
                      <tr><th>Vendor</th><th>POS Plan</th><th>Selected Bundle</th><th>Amount</th><th>Payment</th><th>Start</th><th>Expiry</th><th>Subscription</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {filteredVendorSubscriptions.map((item) => (
                        <tr key={item.subscriptionId}>
                          <td><strong>{item.businessName}</strong><small>{item.mobile || item.ownerName || '—'}</small></td>
                          <td><strong>{item.planName || '—'}</strong><small>#{item.subscriptionId}</small></td>
                          <td>{item.bundleName ? <><strong>{item.bundleName}</strong><small>Order #{item.orderId || '—'}</small></> : <span className="vsub-muted">Plan only</span>}</td>
                          <td><strong>{currency(item.finalAmount ?? item.amount)}</strong><small>{item.bundleName ? 'Bundle purchase' : 'POS charge'}</small></td>
                          <td><span className={`vsub-pay-badge ${item.paymentStatus || 'pending'}`}>{item.paymentStatus || 'pending'}</span></td>
                          <td>{item.startDate || '—'}</td>
                          <td><strong>{item.expiryDate || '—'}</strong><small>{Number(item.daysLeft || 0)} days left</small></td>
                          <td><span className={`vsub-status ${item.status || 'pending'}`}>{item.status || 'pending'}</span></td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              <button type="button" className="vsub-card-action" onClick={() => requestInvoiceApproval(item, 'print')} title="Verify company address and print invoice"><Printer size={14} /> Invoice</button>
                              {String(item.paymentStatus || 'pending').toLowerCase() !== 'paid' && String(item.status || '').toLowerCase() !== 'cancelled' ? (
                                <button type="button" className="vsub-card-action" disabled={saving} onClick={() => markVendorPaid(item)} title="Confirm received payment" style={{ color: '#137a3c', borderColor: '#86d0a3', background: '#f0fdf4' }}><CreditCard size={14} /> Paid</button>
                              ) : (
                                <button type="button" className="vsub-card-action" disabled={saving} onClick={() => requestInvoiceApproval(item, 'download')} title="Verify company address and download paid invoice" style={{ color: '#1252a5', borderColor: '#93c5fd', background: '#eff6ff' }}><FileText size={14} /> Download PDF</button>
                              )}
                              <button type="button" className="vsub-card-action" onClick={() => openVendorAgreement(item)} title="Optional Agreement"><FileText size={14} /> Agreement</button>
                              {String(item.status || '').toLowerCase() === 'pending' && String(item.paymentStatus || 'pending').toLowerCase() !== 'paid' ? (
                                <button type="button" className="vsub-card-action" onClick={() => deletePendingSubscription(item)} title="Delete Pending Subscription"><Trash2 size={14} /> Delete</button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}
        </section>
      </main>
      {showVendorModal ? (
        <div className="vsub-modal-backdrop">
          <form className="vsub-modal" onSubmit={saveVendorAssignment}>
            <div className="vsub-modal-head">
              <div><span>VENDOR ASSIGNMENT</span><h3>Assign Subscription</h3></div>
              <button type="button" onClick={() => { setShowVendorModal(false); resetVendorForm(); }}><X size={20} /></button>
            </div>
            <label>Vendor<select value={vendorForm.vendorKey} onChange={(event) => setVendorForm((current) => ({ ...current, vendorKey: event.target.value }))}><option value="">Select Vendor</option>{availableVendors.map((vendor) => <option key={vendor.vendorKey} value={vendor.vendorKey}>{vendor.businessName} | {vendor.mobile || vendor.ownerName || vendor.vendorKey}</option>)}</select></label>
            <label>Purchase Type<select value={vendorForm.purchaseType} onChange={(event) => setVendorForm((current) => ({ ...current, purchaseType: event.target.value, planId: '', bundleId: '' }))}><option value="plan">POS Plan Only</option><option value="bundle">Bundle</option></select></label>
            {vendorForm.purchaseType === 'plan' ? (
              <label>POS Plan<select value={vendorForm.planId} onChange={(event) => setVendorForm((current) => ({ ...current, planId: event.target.value }))}><option value="">Select POS Plan</option>{plans.filter((item) => item.status === 'active').map((plan) => <option key={plan.id} value={plan.id}>{plan.name} | {plan.durationDays} days | {currency(plan.price)}</option>)}</select></label>
            ) : (
              <label>Bundle<select value={vendorForm.bundleId} onChange={(event) => setVendorForm((current) => ({ ...current, bundleId: event.target.value }))}><option value="">Select Bundle</option>{bundles.filter((item) => item.status === 'active').map((bundle) => <option key={bundle.id} value={bundle.id}>{bundle.name} | {currency(bundle.bundlePrice)}</option>)}</select></label>
            )}
            {(selectedVendorPlan || selectedVendorBundle) ? (
              <>
                <div className="vsub-hardware-picker">
                  <span>Additional Hardware</span>
                  {hardware.filter((item) => item.status === 'active').map((item) => {
                    const selected = vendorForm.additionalHardware.find((selectedItem) => Number(selectedItem.hardwareId) === Number(item.id));
                    return (
                      <div className="vsub-pick-row" key={`vendor-hardware-${item.id}`}>
                        <label className="vsub-check-row">
                          <input type="checkbox" checked={Boolean(selected)} onChange={() => toggleVendorHardware(item.id)} />
                          <div>
                            <strong>{item.name}</strong>
                            <span>{currency(item.price)}{item.warrantyEnabled ? ` • ${item.warrantyMonths} Months Warranty` : ''}</span>
                          </div>
                        </label>
                        {selected ? (
                          <input className="vsub-qty" type="number" min="1" value={selected.quantity} onChange={(event) => updateVendorHardwareQty(item.id, event.target.value)} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className="vsub-hardware-picker">
                  <span>Additional Extra Items</span>
                  {extras.filter((item) => item.status === 'active').map((item) => {
                    const selected = vendorForm.additionalExtras.find((selectedItem) => Number(selectedItem.extraId) === Number(item.id));
                    return (
                      <div className="vsub-pick-row" key={`vendor-extra-${item.id}`}>
                        <label className="vsub-check-row">
                          <input type="checkbox" checked={Boolean(selected)} onChange={() => toggleVendorExtra(item.id)} />
                          <div>
                            <strong>{item.name}</strong>
                            <span>{currency(item.price)} / {item.unit}</span>
                          </div>
                        </label>
                        {selected ? (
                          <input className="vsub-qty" type="number" min="1" value={selected.quantity} onChange={(event) => updateVendorExtraQty(item.id, event.target.value)} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
            {(selectedVendorPlan || selectedVendorBundle) ? (
              <div className="vsub-live-summary">
                <div><span>POS Plan</span><strong>{selectedVendorPlan?.name || '—'}</strong></div>
                <div><span>Duration</span><strong>{selectedVendorPlan?.durationDays || 0} Days</strong></div>
                <div><span>Base Purchase</span><strong>{selectedVendorBundle?.name || 'Plan Only'} · {currency(selectedVendorBaseAmount)}</strong></div>
                <div><span>Additional Hardware</span><strong>{currency(selectedVendorAdditionalHardwareTotal)}</strong></div>
                <div><span>Additional Extras</span><strong>{currency(selectedVendorAdditionalExtraTotal)}</strong></div>
                <div><span>Final Payable</span><strong>{currency(selectedVendorAmount)}</strong></div>
              </div>
            ) : null}
            <label>Start Date<input type="date" value={vendorForm.startDate} onChange={(event) => setVendorForm((current) => ({ ...current, startDate: event.target.value }))} /></label>
            <label>Notes<textarea className="vsub-textarea" value={vendorForm.notes} onChange={(event) => setVendorForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Optional internal note" /></label>
            <div className="vsub-modal-actions"><button type="button" onClick={() => { setShowVendorModal(false); resetVendorForm(); }}>Cancel</button><button type="submit" className="vsub-primary" disabled={saving}><Save size={17} />{saving ? 'Assigning...' : 'Assign Subscription'}</button></div>
          </form>
        </div>
      ) : null}
      {showPlanModal ? (
        <div className="vsub-modal-backdrop">
          <form className="vsub-modal" onSubmit={savePlan}>
            <div className="vsub-modal-head">
              <div>
                <span>POS SOFTWARE</span>
                <h3>
                  {editingPlanId ? 'Edit POS Plan' : 'Add POS Plan'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPlanModal(false);
                  resetPlanForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <label>
              Plan Name
              <input
                value={planForm.name}
                onChange={(event) =>
                  setPlanForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Monthly POS"
              />
            </label>
            <div className="vsub-two">
              <label>
                Duration Days
                <input
                  type="number"
                  min="1"
                  value={planForm.durationDays}
                  onChange={(event) =>
                    setPlanForm((current) => ({
                      ...current,
                      durationDays: event.target.value,
                    }))
                  }
                  placeholder="30"
                />
              </label>
              <label>
                POS Charge
                <input
                  type="number"
                  min="0"
                  value={planForm.price}
                  onChange={(event) =>
                    setPlanForm((current) => ({
                      ...current,
                      price: event.target.value,
                    }))
                  }
                  placeholder="499"
                />
              </label>
            </div>
            <label>
              Status
              <select
                value={planForm.status}
                onChange={(event) =>
                  setPlanForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <div className="vsub-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowPlanModal(false);
                  resetPlanForm();
                }}
              >
                Cancel
              </button>
              <button type="submit" className="vsub-primary" disabled={saving}>
                <Save size={17} />
                {saving ? 'Saving...' : editingPlanId ? 'Update POS Plan' : 'Save POS Plan'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {showHardwareModal ? (
        <div className="vsub-modal-backdrop">
          <form className="vsub-modal" onSubmit={saveHardware}>
            <div className="vsub-modal-head">
              <div>
                <span>HARDWARE</span>
                <h3>
                  {editingHardwareId ? 'Edit Hardware' : 'Add Hardware'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowHardwareModal(false);
                  resetHardwareForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <label>
              Hardware Name
              <input
                value={hardwareForm.name}
                onChange={(event) =>
                  setHardwareForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="58mm Bluetooth Thermal Printer"
              />
            </label>
            <div className="vsub-two">
              <label>
                Hardware Type
                <select
                  value={hardwareForm.type}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      type: event.target.value,
                    }))
                  }
                >
                  <option>Printer</option>
                  <option>POS Device</option>
                  <option>Cash Drawer</option>
                  <option>Scanner</option>
                  <option>Other</option>
                </select>
              </label>
              <label>
                Model
                <input
                  value={hardwareForm.model}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      model: event.target.value,
                    }))
                  }
                  placeholder="58mm BT"
                />
              </label>
            </div>
            <label>
              Product Photo
              <div className="vsub-image-upload">
                {hardwareForm.image ? (
                  <img
                    src={resolveAssetUrl(hardwareForm.image)}
                    alt="Hardware preview"
                  />
                ) : (
                  <div>
                    <ImagePlus size={24} />
                    <span>No photo selected</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    readImageFile(
                      event.target.files?.[0],
                      (image) =>
                        setHardwareForm((current) => ({
                          ...current,
                          image,
                        }))
                    )
                  }
                />
              </div>
            </label>
            <label>
              Items Included in the Box
              <textarea
                className="vsub-textarea"
                value={hardwareForm.includedText}
                onChange={(event) =>
                  setHardwareForm((current) => ({
                    ...current,
                    includedText: event.target.value,
                  }))
                }
                placeholder={'Power Adapter\nUSB Cable\n1 Thermal Roll'}
              />
              <small>
                Enter each item on a separate line.
              </small>
            </label>
            <div className="vsub-two">
              <label>
                Warranty
                <select
                  value={hardwareForm.warrantyEnabled ? 'yes' : 'no'}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      warrantyEnabled: event.target.value === 'yes',
                      warrantyMonths:
                        event.target.value === 'yes' ? current.warrantyMonths : '',
                      warrantyType:
                        event.target.value === 'yes'
                          ? current.warrantyType || 'Manufacturer Warranty'
                          : '',
                      warrantyTerms:
                        event.target.value === 'yes' ? current.warrantyTerms : '',
                    }))
                  }
                >
                  <option value="no">No Warranty</option>
                  <option value="yes">Warranty Included</option>
                </select>
              </label>
              <label>
                Warranty Months
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={hardwareForm.warrantyMonths}
                  disabled={!hardwareForm.warrantyEnabled}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      warrantyMonths: event.target.value,
                    }))
                  }
                  placeholder={hardwareForm.warrantyEnabled ? '12' : 'Not Applicable'}
                />
              </label>
            </div>
            {hardwareForm.warrantyEnabled ? (
              <>
                <label>
                  Warranty Type
                  <select
                    value={hardwareForm.warrantyType}
                    onChange={(event) =>
                      setHardwareForm((current) => ({
                        ...current,
                        warrantyType: event.target.value,
                      }))
                    }
                  >
                    <option>Manufacturer Warranty</option>
                    <option>Seller Warranty</option>
                    <option>Service Warranty</option>
                    <option>Replacement Warranty</option>
                    <option>Other</option>
                  </select>
                </label>
                <label>
                  Warranty Terms
                  <textarea
                    className="vsub-textarea"
                    value={hardwareForm.warrantyTerms}
                    onChange={(event) =>
                      setHardwareForm((current) => ({
                        ...current,
                        warrantyTerms: event.target.value,
                      }))
                    }
                    placeholder={'Manufacturing defects covered.\nPhysical or liquid damage not covered.\nOriginal invoice required.'}
                  />
                  <small>Enter important warranty terms and conditions here.</small>
                </label>
              </>
            ) : null}
            <div className="vsub-two">
              <label>
                Individual Price
                <input
                  type="number"
                  min="0"
                  value={hardwareForm.price}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      price: event.target.value,
                    }))
                  }
                  placeholder="2499"
                />
              </label>
              <label>
                Status
                <select
                  value={hardwareForm.status}
                  onChange={(event) =>
                    setHardwareForm((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
            <div className="vsub-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowHardwareModal(false);
                  resetHardwareForm();
                }}
              >
                Cancel
              </button>
              <button type="submit" className="vsub-primary" disabled={saving}>
                <Save size={17} />
                {saving ? 'Saving...' : editingHardwareId ? 'Update Hardware' : 'Save Hardware'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {showExtraModal ? (
        <div className="vsub-modal-backdrop">
          <form className="vsub-modal" onSubmit={saveExtra}>
            <div className="vsub-modal-head">
              <div>
                <span>ADDITIONAL ITEMS</span>
                <h3>
                  {editingExtraId ? 'Edit Extra Item' : 'Add Extra Item'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowExtraModal(false);
                  resetExtraForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <label>
              Item Name
              <input
                value={extraForm.name}
                onChange={(event) =>
                  setExtraForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Thermal Paper Roll 58mm"
              />
            </label>
            <div className="vsub-two">
              <label>
                Type
                <select
                  value={extraForm.type}
                  onChange={(event) =>
                    setExtraForm((current) => ({
                      ...current,
                      type: event.target.value,
                    }))
                  }
                >
                  <option>Accessory</option>
                  <option>Consumable</option>
                  <option>Spare Part</option>
                  <option>Service</option>
                  <option>Other</option>
                </select>
              </label>
              <label>
                Unit
                <input
                  value={extraForm.unit}
                  onChange={(event) =>
                    setExtraForm((current) => ({
                      ...current,
                      unit: event.target.value,
                    }))
                  }
                  placeholder="pcs / roll / box"
                />
              </label>
            </div>
            <label>
              Photo
              <div className="vsub-image-upload">
                {extraForm.image ? (
                  <img src={resolveAssetUrl(extraForm.image)} alt="Extra preview" />
                ) : (
                  <div>
                    <ImagePlus size={24} />
                    <span>No photo selected</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) =>
                    readImageFile(
                      event.target.files?.[0],
                      (image) =>
                        setExtraForm((current) => ({
                          ...current,
                          image,
                        }))
                    )
                  }
                />
              </div>
            </label>
            <div className="vsub-two">
              <label>
                Price
                <input
                  type="number"
                  min="0"
                  value={extraForm.price}
                  onChange={(event) =>
                    setExtraForm((current) => ({
                      ...current,
                      price: event.target.value,
                    }))
                  }
                  placeholder="35"
                />
              </label>
              <label>
                Status
                <select
                  value={extraForm.status}
                  onChange={(event) =>
                    setExtraForm((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>
            <div className="vsub-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowExtraModal(false);
                  resetExtraForm();
                }}
              >
                Cancel
              </button>
              <button type="submit" className="vsub-primary" disabled={saving}>
                <Save size={17} />
                {saving ? 'Saving...' : editingExtraId ? 'Update Extra Item' : 'Save Extra Item'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {showBundleModal ? (
        <div className="vsub-modal-backdrop">
          <form className="vsub-modal large" onSubmit={saveBundle}>
            <div className="vsub-modal-head">
              <div>
                <span>POS + HARDWARE</span>
                <h3>
                  {editingBundleId ? 'Edit Bundle' : 'Create Bundle'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowBundleModal(false);
                  resetBundleForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            <label>
              Bundle Name
              <input
                value={bundleForm.name}
                onChange={(event) =>
                  setBundleForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="Yearly POS + 58mm Printer"
              />
            </label>
            <label>
              Select POS Plan
              <select
                value={bundleForm.planId}
                onChange={(event) =>
                  setBundleForm((current) => ({
                    ...current,
                    planId: event.target.value,
                  }))
                }
              >
                <option value="">Select POS Plan</option>
                {plans
                  .filter((item) => item.status === 'active')
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} - {currency(item.price)}
                    </option>
                  ))}
              </select>
            </label>
            <div className="vsub-hardware-picker">
              <span>Select Hardware</span>
              {hardware
                .filter((item) => item.status === 'active')
                .map((item) => {
                  const selected = bundleForm.selectedHardware.find(
                    (selectedItem) =>
                      Number(selectedItem.hardwareId) === Number(item.id)
                  );
                  return (
                    <div className="vsub-pick-row" key={item.id}>
                      <label className="vsub-check-row">
                        <input
                          type="checkbox"
                          checked={Boolean(selected)}
                          onChange={() => toggleBundleHardware(item.id)}
                        />
                        <div>
                          <strong>{item.name}</strong>
                          <span>{currency(item.price)}</span>
                        </div>
                      </label>
                      {selected ? (
                        <input
                          className="vsub-qty"
                          type="number"
                          min="1"
                          value={selected.quantity}
                          onChange={(event) =>
                            updateBundleHardwareQty(
                              item.id,
                              event.target.value
                            )
                          }
                        />
                      ) : null}
                    </div>
                  );
                })}
            </div>
            <div className="vsub-hardware-picker">
              <span>Optional Extra Items</span>
              {extras
                .filter((item) => item.status === 'active')
                .map((item) => {
                  const selected = bundleForm.selectedExtras.find(
                    (selectedItem) =>
                      Number(selectedItem.extraId) === Number(item.id)
                  );
                  return (
                    <div className="vsub-pick-row" key={item.id}>
                      <label className="vsub-check-row">
                        <input
                          type="checkbox"
                          checked={Boolean(selected)}
                          onChange={() => toggleBundleExtra(item.id)}
                        />
                        <div>
                          <strong>{item.name}</strong>
                          <span>
                            {currency(item.price)} / {item.unit}
                          </span>
                        </div>
                      </label>
                      {selected ? (
                        <input
                          className="vsub-qty"
                          type="number"
                          min="1"
                          value={selected.quantity}
                          onChange={(event) =>
                            updateBundleExtraQty(
                              item.id,
                              event.target.value
                            )
                          }
                        />
                      ) : null}
                    </div>
                  );
                })}
            </div>
            <div className="vsub-pricing-mode">
              <button
                type="button"
                className={
                  bundleForm.pricingMode === 'final' ? 'active' : ''
                }
                onClick={() =>
                  setBundleForm((current) => ({
                    ...current,
                    pricingMode: 'final',
                  }))
                }
              >
                Final Bundle Amount
              </button>
              <button
                type="button"
                className={
                  bundleForm.pricingMode === 'percent' ? 'active' : ''
                }
                onClick={() =>
                  setBundleForm((current) => ({
                    ...current,
                    pricingMode: 'percent',
                  }))
                }
              >
                Discount %
              </button>
            </div>
            {bundleForm.pricingMode === 'final' ? (
              <label>
                Final Bundle Amount
                <input
                  type="number"
                  min="0"
                  value={bundleForm.finalPrice}
                  onChange={(event) =>
                    setBundleForm((current) => ({
                      ...current,
                      finalPrice: event.target.value,
                    }))
                  }
                  placeholder="5500"
                />
              </label>
            ) : (
              <label>
                Discount Percent
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={bundleForm.discountPercent}
                  onChange={(event) =>
                    setBundleForm((current) => ({
                      ...current,
                      discountPercent: event.target.value,
                    }))
                  }
                  placeholder="15"
                />
              </label>
            )}
            <div className="vsub-live-summary">
              <div>
                <span>Normal Total</span>
                <strong>{currency(calculatedBundle.normalTotal)}</strong>
              </div>
              <div>
                <span>Bundle Price</span>
                <strong>{currency(calculatedBundle.finalPrice)}</strong>
              </div>
              <div>
                <span>You Save</span>
                <strong>{currency(calculatedBundle.saving)}</strong>
              </div>
              <div>
                <span>Discount</span>
                <strong>
                  {calculatedBundle.discountPercent.toFixed(2)}%
                </strong>
              </div>
            </div>
            <div className="vsub-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowBundleModal(false);
                  resetBundleForm();
                }}
              >
                Cancel
              </button>
              <button type="submit" className="vsub-primary" disabled={saving}>
                <Save size={17} />
                {saving ? 'Saving...' : editingBundleId ? 'Update Bundle' : 'Save Bundle'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {invoiceApproval ? (
        <div className="vsub-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.65)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-label="Verify company address" style={{ background: '#fff', borderRadius: 14, maxWidth: 520, width: '100%', padding: 24, boxShadow: '0 18px 65px rgba(0,0,0,.25)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 14 }}>VAKRA Company Address Verification</h3>
            <p style={{ fontSize: 14, margin: '6px 0' }}><strong>Address:</strong> {VAKRA_COMPANY_ADDRESS}</p>
            <p style={{ fontSize: 14, margin: '6px 0 18px' }}><strong>Email:</strong> {VAKRA_COMPANY_EMAIL}</p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={invoiceAddressChecked} onChange={(event) => setInvoiceAddressChecked(event.target.checked)} style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1 }} />
              <span>I have verified that the VAKRA company address and email are correct.</span>
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
              <button type="button" onClick={() => { setInvoiceApproval(null); setInvoiceAddressChecked(false); }} style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={!invoiceAddressChecked || saving} onClick={confirmInvoiceApproval} style={{ padding: '10px 16px', border: 0, borderRadius: 8, background: invoiceAddressChecked ? '#1457ba' : '#9ca3af', color: '#fff', cursor: invoiceAddressChecked ? 'pointer' : 'not-allowed' }}>
                {invoiceApproval.action === 'print' ? 'Verify & Print Invoice' : 'Verify & Download PDF'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
export default ControlCenterSubscriptions;
